import React from "react";
import PropTypes from "prop-types";
import Downshift from "downshift";
import classNames from "classnames";
import lowerCase from "lodash/lowerCase";
import Item from "./Item";
import Checkbox from "./Checkbox";
import { artistShape } from "../shapes";
import { CaretIcon, SearchIcon, CloseIcon } from "../icons";
import { artistDates } from "./shared";

const itemToString = (artist) =>
  artist ? `${artist.name} ${artistDates(artist)}` : "";

function filterArtists(artist, inputValue) {
  if (!inputValue) return true;
  return lowerCase(artist.name).includes(lowerCase(inputValue));
}

function hasArtists(artists, inputValue) {
  const filtered = artists.filter((artist) =>
    filterArtists(artist, inputValue || null)
  );
  return filtered.length > 0;
}

function ArtistComboBox({
  artists,
  onChange,
  label,
  placeholder,
  wrapperClass,
  theme,
  checkboxes,
}) {
  if (artists.length <= 0) return null;

  return (
    <Downshift onChange={onChange} itemToString={itemToString}>
      {({
        getInputProps,
        getItemProps,
        getLabelProps,
        getMenuProps,
        isOpen,
        inputValue,
        highlightedIndex,
        selectedItem,
        getRootProps,
        clearSelection,
        getToggleButtonProps,
      }) => {
        const filteredArtists = artists.filter((artist) =>
          filterArtists(artist, inputValue)
        );
        const showCheckboxes =
          isOpen &&
          filteredArtists.length > 0 &&
          checkboxes &&
          checkboxes.length > 0;
        return (
          <div
            className={classNames(
              `m-artist-search m-artist-search--${theme} ${wrapperClass}`
            )}
          >
            <label className="a-hidden" {...getLabelProps()}>
              {label}
            </label>
            <div
              className="m-artist-search__combobox"
              {...getRootProps({}, { suppressRefError: true })}
            >
              <input
                className="m-artist-search__input"
                placeholder={placeholder}
                spellCheck="false"
                {...getInputProps()}
              />
              {selectedItem ? (
                <button
                  onClick={clearSelection}
                  aria-label="clear selection"
                  className="m-artist-search__button"
                >
                  <CloseIcon />
                </button>
              ) : (
                <button
                  {...getToggleButtonProps()}
                  className="m-artist-search__button"
                >
                  <CaretIcon
                    styleProps={{
                      transform: isOpen ? undefined : "rotate(180deg)",
                    }}
                  />
                </button>
              )}
            </div>
            <div
              className={classNames({
                "m-artist-search__listbox-container": true,
                "m-artist-search__listbox-container--is-open":
                  isOpen && hasArtists(artists, inputValue),
              })}
            >
              <div
                className={classNames({
                  "m-artist-search__listbox": true,
                  "m-artist-search__listbox--is-open":
                    isOpen && hasArtists(artists, inputValue),
                })}
                {...getMenuProps()}
              >
                {isOpen && (
                  <div className="m-artist-search__listbox-content">
                    {showCheckboxes && (
                      <div className="m-artist-search__checkbox-group">
                        {checkboxes.map((checkbox) => (
                          <Checkbox key={checkbox.id} {...checkbox} />
                        ))}
                      </div>
                    )}
                    {filteredArtists.map((artist, index) => (
                      <Item
                        key={artist.id}
                        index={index}
                        artist={artist}
                        itemProps={getItemProps}
                        highlighted={highlightedIndex === index}
                        selected={selectedItem === artist}
                        theme={theme}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
            <SearchIcon
              size={theme === "subnav" ? 16 : 36}
              className="m-artist-search__search-icon"
            />
          </div>
        );
      }}
    </Downshift>
  );
}

ArtistComboBox.propTypes = {
  artists: PropTypes.arrayOf(PropTypes.shape(artistShape)),
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  wrapperClass: PropTypes.string,
  theme: PropTypes.oneOf(["default", "subnav"]),
  checkboxes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      parameter: PropTypes.string.isRequired,
      checked: PropTypes.bool.isRequired,
      onChange: PropTypes.func.isRequired,
    })
  ),
};

ArtistComboBox.defaultProps = {
  theme: "default",
};

export default ArtistComboBox;
